import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import {
  Checkbox,
  Collapse,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import {
  DEFAULT_DEV_FACTORS,
  DEFAULT_INCLUDE,
  DEFAULT_TARGET_LOSS_RATIO,
  DEFAULT_YEAR_COUNT,
} from "components/lossRating/lossRatingConstants";
import { useCallback, useEffect, useRef, useState } from "react";

const FONT_SIZE = "0.875rem";

const TF = {
  variant: "filled",
  size: "small",
  fullWidth: true,
  InputLabelProps: { shrink: true },
};

const inputSx = {
  "& .MuiInputBase-input": {
    fontSize: FONT_SIZE,
    textAlign: "right",
    py: "4px",
    px: 1,
  },
};

const cellSx = { fontSize: FONT_SIZE, py: 0.25, px: 1 };
const headSx = {
  fontSize: FONT_SIZE,
  fontWeight: 600,
  py: 0.75,
  px: 1,
  whiteSpace: "nowrap",
};

const SectionBar = ({ label, open, onToggle }) => (
  <Stack
    direction={"row"}
    alignItems={"center"}
    justifyContent={"space-between"}
    onClick={onToggle}
    sx={{
      backgroundColor: "grey.100",
      cursor: "pointer",
      px: 2,
      py: 1.25,
      borderRadius: 1,
    }}
  >
    <Typography fontSize={FONT_SIZE} fontWeight={700} color={"text.secondary"}>
      {label}
    </Typography>
    {open ? (
      <ExpandLessIcon sx={{ fontSize: 18, color: "text.secondary" }} />
    ) : (
      <ExpandMoreIcon sx={{ fontSize: 18, color: "text.secondary" }} />
    )}
  </Stack>
);

const getEffectiveYears = (stored) => {
  const years = [];
  for (let i = 0; i < DEFAULT_YEAR_COUNT; i++) {
    const yr = stored?.[i];
    years.push({
      include: yr?.include ?? DEFAULT_INCLUDE[i] ?? true,
      exposure: yr?.exposure ?? null,
      openClaimCount: yr?.openClaimCount ?? null,
      closedClaimCount: yr?.closedClaimCount ?? null,
      incurredOpen: yr?.incurredOpen ?? null,
      incurredClosed: yr?.incurredClosed ?? null,
      developmentFactor: yr?.developmentFactor ?? DEFAULT_DEV_FACTORS[i],
    });
  }
  return years;
};

const LossRatingInputs = ({ data, onUpdate }) => {
  const [open, setOpen] = useState(false);
  const [scrollShadow, setScrollShadow] = useState({
    left: false,
    right: false,
  });
  const tableContainerRef = useRef(null);

  const updateScrollShadow = useCallback(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    setScrollShadow({
      left: el.scrollLeft > 0,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
    });
  }, []);

  useEffect(() => {
    updateScrollShadow();
  }, [updateScrollShadow]);

  const projectedExposure = data?.projectedExposure ?? null;
  const annualTrend = data?.annualTrendFactor ?? 1.08;
  // Unset means the engine will use its own default, so show that default
  // rather than an empty box the underwriter has to fill in every time.
  const targetLossRatio = data?.targetLossRatio ?? DEFAULT_TARGET_LOSS_RATIO;
  const years = getEffectiveYears(data?.policyYears);

  const updateYear = (i, field, value) => {
    const updated = years.map((y, idx) =>
      idx === i ? { ...y, [field]: value } : y
    );
    onUpdate("policyYears", updated);
  };

  const totals = years.reduce(
    (acc, yr, i) => {
      const yearIndex = i + 1;
      const totalIncurred = (yr.incurredOpen ?? 0) + (yr.incurredClosed ?? 0);
      const trend = Math.pow(annualTrend, yearIndex);
      const dev = yr.developmentFactor ?? DEFAULT_DEV_FACTORS[i];
      const exp = yr.exposure ?? projectedExposure ?? 0;
      const expFactor = exp > 0 ? (projectedExposure ?? 0) / exp : 1;
      const ultimateLoss = totalIncurred * trend * dev * expFactor;
      const included = yr.include !== false;
      return {
        open: acc.open + (yr.openClaimCount ?? 0),
        closed: acc.closed + (yr.closedClaimCount ?? 0),
        incurredOpen: acc.incurredOpen + (yr.incurredOpen ?? 0),
        incurredClosed: acc.incurredClosed + (yr.incurredClosed ?? 0),
        totalIncurred: acc.totalIncurred + totalIncurred,
        ultimateLoss: acc.ultimateLoss + ultimateLoss,
        includedUltimateLoss:
          acc.includedUltimateLoss + (included ? ultimateLoss : 0),
        includedExpFactor: acc.includedExpFactor + (included ? expFactor : 0),
        includedCount: acc.includedCount + (included ? 1 : 0),
      };
    },
    {
      open: 0,
      closed: 0,
      incurredOpen: 0,
      incurredClosed: 0,
      totalIncurred: 0,
      ultimateLoss: 0,
      includedUltimateLoss: 0,
      includedExpFactor: 0,
      includedCount: 0,
    }
  );
  const n = years.length;
  const avgs = {
    open: n > 0 ? totals.open / n : 0,
    closed: n > 0 ? totals.closed / n : 0,
    incurredOpen: n > 0 ? totals.incurredOpen / n : 0,
    incurredClosed: n > 0 ? totals.incurredClosed / n : 0,
    totalIncurred: n > 0 ? totals.totalIncurred / n : 0,
    expFactor:
      totals.includedCount > 0
        ? totals.includedExpFactor / totals.includedCount
        : 0,
    ultimateLoss:
      totals.includedCount > 0
        ? totals.includedUltimateLoss / totals.includedCount
        : 0,
  };

  return (
    <Stack spacing={1.5}>
      <SectionBar
        label={"Rating Parameters"}
        open={open}
        onToggle={() => setOpen((v) => !v)}
      />
      <Collapse in={open} timeout={"auto"} unmountOnExit>
        <Stack spacing={1} sx={{ pt: 0.5, pb: 1, px: 1 }}>
          <PrettyNumberTextField
            {...TF}
            label={"Projected Exposure ($)"}
            value={data?.projectedExposure ?? null}
            onChangeNumber={(v) => onUpdate("projectedExposure", v)}
          />
          <PrettyNumberTextField
            {...TF}
            label={"Annual Trend Factor"}
            value={data?.annualTrendFactor ?? 1.08}
            onChangeNumber={(v) => onUpdate("annualTrendFactor", v)}
          />
          <PrettyNumberTextField
            {...TF}
            label={"Target Loss Ratio (%)"}
            value={Math.round(targetLossRatio * 10000) / 100}
            onChangeNumber={(v) =>
              onUpdate("targetLossRatio", v == null ? null : v / 100)
            }
          />
          <PrettyNumberTextField
            {...TF}
            label={"Deductible ($)"}
            value={data?.deductible ?? null}
            onChangeNumber={(v) => onUpdate("deductible", v)}
          />
        </Stack>
      </Collapse>

      {/* Policy Year Loss Data — always visible */}
      <Stack spacing={0.75}>
        <Typography
          variant={"overline"}
          color={"text.secondary"}
          sx={{ letterSpacing: 1, lineHeight: 1, fontSize: "0.7rem" }}
        >
          {"Policy Year Loss Data"}
        </Typography>
        <div style={{ position: "relative" }}>
          {scrollShadow.left && (
            <div
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 32,
                zIndex: 1,
                pointerEvents: "none",
                background:
                  "linear-gradient(to right, rgba(0,0,0,0.08), transparent)",
              }}
            />
          )}
          {scrollShadow.right && (
            <div
              style={{
                position: "absolute",
                right: 0,
                top: 0,
                bottom: 0,
                width: 32,
                zIndex: 1,
                pointerEvents: "none",
                background:
                  "linear-gradient(to left, rgba(0,0,0,0.08), transparent)",
              }}
            />
          )}
          <TableContainer ref={tableContainerRef} onScroll={updateScrollShadow}>
            <Table size={"small"} sx={{ tableLayout: "auto" }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "grey.50" }}>
                  <TableCell sx={headSx}>{"Year"}</TableCell>
                  <TableCell sx={{ ...headSx, textAlign: "center" }}>
                    {"Include"}
                  </TableCell>
                  <TableCell sx={headSx} align={"right"}>
                    {"Exposure ($)"}
                  </TableCell>
                  <TableCell sx={headSx} align={"right"}>
                    {"Open"}
                  </TableCell>
                  <TableCell sx={headSx} align={"right"}>
                    {"Closed"}
                  </TableCell>
                  <TableCell
                    sx={{ ...headSx, color: "text.secondary" }}
                    align={"right"}
                  >
                    {"Total"}
                  </TableCell>
                  <TableCell sx={headSx} align={"right"}>
                    {"Inc. Open ($)"}
                  </TableCell>
                  <TableCell sx={headSx} align={"right"}>
                    {"Inc. Closed ($)"}
                  </TableCell>
                  <TableCell
                    sx={{ ...headSx, color: "text.secondary" }}
                    align={"right"}
                  >
                    {"Total Inc."}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...headSx,
                      color: "text.secondary",
                      whiteSpace: "normal",
                      maxWidth: 60,
                    }}
                    align={"right"}
                  >
                    {"Trend Factor"}
                  </TableCell>
                  <TableCell
                    sx={{ ...headSx, color: "text.secondary" }}
                    align={"right"}
                  >
                    {"Dev. Factor"}
                  </TableCell>
                  <TableCell
                    sx={{ ...headSx, color: "text.secondary" }}
                    align={"right"}
                  >
                    {"T&D"}
                  </TableCell>
                  <TableCell
                    sx={{ ...headSx, color: "text.secondary" }}
                    align={"right"}
                  >
                    {"Exp. Factor"}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...headSx,
                      color: "text.secondary",
                      whiteSpace: "normal",
                      maxWidth: 60,
                    }}
                    align={"right"}
                  >
                    {"Ultimate Loss"}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {years.map((yr, i) => {
                  const yearIndex = i + 1;
                  const policyYear = new Date().getFullYear() - yearIndex;
                  const totalClaims =
                    (yr.openClaimCount ?? 0) + (yr.closedClaimCount ?? 0);
                  const totalIncurred =
                    (yr.incurredOpen ?? 0) + (yr.incurredClosed ?? 0);
                  const trend = Math.pow(annualTrend, yearIndex);
                  const dev = yr.developmentFactor ?? DEFAULT_DEV_FACTORS[i];
                  const tAndD = trend * dev;
                  const exp = yr.exposure ?? projectedExposure ?? 0;
                  const expFactor =
                    exp > 0 ? (projectedExposure ?? 0) / exp : 1;
                  const ultimateLoss = totalIncurred * tAndD * expFactor;
                  const dimSx = yr.include ? {} : { opacity: 0.45 };

                  return (
                    <TableRow key={i} hover sx={dimSx}>
                      <TableCell sx={cellSx}>
                        <Typography
                          fontSize={FONT_SIZE}
                          color={"text.secondary"}
                        >
                          {policyYear}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ ...cellSx, textAlign: "center" }}>
                        <Checkbox
                          size={"small"}
                          checked={yr.include !== false}
                          onChange={(e) =>
                            updateYear(i, "include", e.target.checked)
                          }
                          sx={{ p: 0.25 }}
                        />
                      </TableCell>
                      <TableCell sx={{ ...cellSx, minWidth: 110 }}>
                        <PrettyNumberTextField
                          hiddenLabel
                          size={"small"}
                          variant={"filled"}
                          value={yr.exposure ?? projectedExposure ?? null}
                          onChangeNumber={(v) => updateYear(i, "exposure", v)}
                          sx={inputSx}
                        />
                      </TableCell>
                      <TableCell sx={{ ...cellSx, minWidth: 70 }}>
                        <PrettyNumberTextField
                          hiddenLabel
                          size={"small"}
                          variant={"filled"}
                          value={yr.openClaimCount ?? null}
                          onChangeNumber={(v) =>
                            updateYear(i, "openClaimCount", v)
                          }
                          sx={inputSx}
                        />
                      </TableCell>
                      <TableCell sx={{ ...cellSx, minWidth: 70 }}>
                        <PrettyNumberTextField
                          hiddenLabel
                          size={"small"}
                          variant={"filled"}
                          value={yr.closedClaimCount ?? null}
                          onChangeNumber={(v) =>
                            updateYear(i, "closedClaimCount", v)
                          }
                          sx={inputSx}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          ...cellSx,
                          minWidth: 50,
                          textAlign: "right",
                          color: "text.secondary",
                        }}
                      >
                        {totalClaims || ""}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, minWidth: 90 }}>
                        <PrettyNumberTextField
                          hiddenLabel
                          size={"small"}
                          variant={"filled"}
                          value={yr.incurredOpen ?? null}
                          onChangeNumber={(v) =>
                            updateYear(i, "incurredOpen", v)
                          }
                          sx={inputSx}
                        />
                      </TableCell>
                      <TableCell sx={{ ...cellSx, minWidth: 90 }}>
                        <PrettyNumberTextField
                          hiddenLabel
                          size={"small"}
                          variant={"filled"}
                          value={yr.incurredClosed ?? null}
                          onChangeNumber={(v) =>
                            updateYear(i, "incurredClosed", v)
                          }
                          sx={inputSx}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          ...cellSx,
                          minWidth: 80,
                          textAlign: "right",
                          color: "text.secondary",
                        }}
                      >
                        {totalIncurred > 0
                          ? totalIncurred.toLocaleString()
                          : ""}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...cellSx,
                          minWidth: 60,
                          textAlign: "right",
                          color: "text.secondary",
                        }}
                      >
                        {trend.toFixed(2)}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...cellSx,
                          minWidth: 60,
                          textAlign: "right",
                          color: "text.secondary",
                        }}
                      >
                        {dev.toFixed(2)}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...cellSx,
                          minWidth: 60,
                          textAlign: "right",
                          color: "text.secondary",
                        }}
                      >
                        {tAndD.toFixed(2)}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...cellSx,
                          minWidth: 60,
                          textAlign: "right",
                          color: "text.secondary",
                        }}
                      >
                        {expFactor.toFixed(2)}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...cellSx,
                          minWidth: 100,
                          textAlign: "right",
                          color: "text.secondary",
                          fontWeight: yr.include !== false ? 600 : 400,
                        }}
                      >
                        {ultimateLoss > 0
                          ? Math.round(ultimateLoss).toLocaleString()
                          : ""}
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow
                  sx={{
                    borderTop: "2px solid",
                    borderColor: "divider",
                    backgroundColor: "grey.50",
                  }}
                >
                  <TableCell sx={{ ...cellSx, fontWeight: 600 }} colSpan={2}>
                    {"Sum"}
                  </TableCell>
                  <TableCell sx={cellSx} />
                  <TableCell
                    sx={{ ...cellSx, textAlign: "right", fontWeight: 600 }}
                  >
                    {totals.open || ""}
                  </TableCell>
                  <TableCell
                    sx={{ ...cellSx, textAlign: "right", fontWeight: 600 }}
                  >
                    {totals.closed || ""}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...cellSx,
                      textAlign: "right",
                      fontWeight: 600,
                      color: "text.secondary",
                    }}
                  >
                    {totals.open + totals.closed || ""}
                  </TableCell>
                  <TableCell
                    sx={{ ...cellSx, textAlign: "right", fontWeight: 600 }}
                  >
                    {totals.incurredOpen > 0
                      ? totals.incurredOpen.toLocaleString()
                      : ""}
                  </TableCell>
                  <TableCell
                    sx={{ ...cellSx, textAlign: "right", fontWeight: 600 }}
                  >
                    {totals.incurredClosed > 0
                      ? totals.incurredClosed.toLocaleString()
                      : ""}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...cellSx,
                      textAlign: "right",
                      fontWeight: 600,
                      color: "text.secondary",
                    }}
                  >
                    {totals.totalIncurred > 0
                      ? totals.totalIncurred.toLocaleString()
                      : ""}
                  </TableCell>
                  <TableCell sx={cellSx} colSpan={3} />
                  <TableCell
                    sx={{
                      ...cellSx,
                      textAlign: "right",
                      fontWeight: 600,
                      color: "text.secondary",
                    }}
                  >
                    {totals.includedExpFactor > 0
                      ? totals.includedExpFactor.toFixed(2)
                      : ""}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...cellSx,
                      textAlign: "right",
                      fontWeight: 700,
                      color: "text.primary",
                    }}
                  >
                    {totals.ultimateLoss > 0
                      ? Math.round(totals.ultimateLoss).toLocaleString()
                      : ""}
                  </TableCell>
                </TableRow>
                <TableRow sx={{ backgroundColor: "grey.50" }}>
                  <TableCell
                    sx={{
                      ...cellSx,
                      color: "text.secondary",
                      fontStyle: "italic",
                    }}
                    colSpan={2}
                  >
                    {"Avg."}
                  </TableCell>
                  <TableCell sx={cellSx} />
                  <TableCell
                    sx={{
                      ...cellSx,
                      textAlign: "right",
                      fontStyle: "italic",
                      color: "text.secondary",
                    }}
                  >
                    {avgs.open > 0 ? avgs.open.toFixed(1) : ""}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...cellSx,
                      textAlign: "right",
                      fontStyle: "italic",
                      color: "text.secondary",
                    }}
                  >
                    {avgs.closed > 0 ? avgs.closed.toFixed(1) : ""}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...cellSx,
                      textAlign: "right",
                      fontStyle: "italic",
                      color: "text.secondary",
                    }}
                  >
                    {avgs.open + avgs.closed > 0
                      ? (avgs.open + avgs.closed).toFixed(1)
                      : ""}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...cellSx,
                      textAlign: "right",
                      fontStyle: "italic",
                      color: "text.secondary",
                    }}
                  >
                    {avgs.incurredOpen > 0
                      ? Math.round(avgs.incurredOpen).toLocaleString()
                      : ""}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...cellSx,
                      textAlign: "right",
                      fontStyle: "italic",
                      color: "text.secondary",
                    }}
                  >
                    {avgs.incurredClosed > 0
                      ? Math.round(avgs.incurredClosed).toLocaleString()
                      : ""}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...cellSx,
                      textAlign: "right",
                      fontStyle: "italic",
                      color: "text.secondary",
                    }}
                  >
                    {avgs.totalIncurred > 0
                      ? Math.round(avgs.totalIncurred).toLocaleString()
                      : ""}
                  </TableCell>
                  <TableCell sx={cellSx} colSpan={3} />
                  <TableCell
                    sx={{
                      ...cellSx,
                      textAlign: "right",
                      fontStyle: "italic",
                      color: "text.secondary",
                    }}
                  >
                    {avgs.expFactor > 0 ? avgs.expFactor.toFixed(2) : ""}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...cellSx,
                      textAlign: "right",
                      fontStyle: "italic",
                      fontWeight: 600,
                      color: "text.secondary",
                    }}
                  >
                    {avgs.ultimateLoss > 0
                      ? Math.round(avgs.ultimateLoss).toLocaleString()
                      : ""}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </Stack>
    </Stack>
  );
};

export default LossRatingInputs;
